
import React, { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AgentCard from "@/components/agent-card";
import FadeInWhenVisible from "./FadeInWhenVisible";
import { AgentDiscoveryService } from "@/services/statistics/agent-discovery-service";
import { Skeleton } from "@/components/ui/skeleton";

const AgentShowcaseSection: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState("productivity");
  const [agents, setAgents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAgentsByCategory = async () => {
      try {
        setLoading(true);
        const data = await AgentDiscoveryService.getAgentsByCategory(activeCategory, 4);
        setAgents(data);
      } catch (error) {
        console.error("Failed to fetch agents:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAgentsByCategory();
  }, [activeCategory]);

  return (
    <section className="py-20 bg-white dark:bg-gray-950">
      <div className="container mx-auto px-4">
        <FadeInWhenVisible>
          <div className="text-center mb-12">
            <Badge className="mb-2">Explore by Use Case</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 dark:text-white">
              AI Agents for Every Need
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Discover specialized AI agents designed for specific industries and use cases
            </p>
          </div>
        </FadeInWhenVisible>

        <Tabs defaultValue="productivity" onValueChange={setActiveCategory} className="w-full">
          <div className="flex justify-center mb-8">
            <TabsList className="bg-gray-100 dark:bg-gray-800 p-1">
              <TabsTrigger
                value="productivity"
                className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700"
              >
                Productivity
              </TabsTrigger>
              <TabsTrigger
                value="business"
                className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700"
              >
                Business
              </TabsTrigger>
              <TabsTrigger
                value="creative"
                className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700"
              >
                Creative
              </TabsTrigger>
              <TabsTrigger
                value="development"
                className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700"
              >
                Development
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {loading ? (
              Array(4).fill(0).map((_, index) => (
                <div key={index} className="space-y-4">
                  <Skeleton className="h-48 w-full rounded-lg" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ))
            ) : agents.length > 0 ? (
              agents.map((agent) => (
                <AgentCard 
                  key={agent.id} 
                  agent={{
                    ...agent,
                    creatorName: agent.profiles?.name || "Anonymous Creator"
                  }} 
                />
              ))
            ) : (
              <div className="col-span-full text-center py-10">
                <p className="text-gray-500">No agents found for this category.</p>
              </div>
            )}
          </div>
        </Tabs>
      </div>
    </section>
  );
};

export default AgentShowcaseSection;
